import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardsItemIcon,
  ExpensesDetailsIcon,
  HomeOutlineIcon,
  MyAccountsIcon,
  MyDocumentsIcon,
  PayMyBillIcon,
  ReimburseMeIcon,
  useResponsive,
} from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import ROUTES from '@/common/routes';
import { LimitedAccessLabel, NoAccountsAvailableLabel } from '@/components/elements';
import { Option } from '@/modules/core/components/Menu/menu.types';
import { useHasAccess } from '@/modules/core/hooks';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import useMyEnrollments from '@/modules/transaction/hooks/useMyEnrollments';
import { useMyMoneyReimbursementFeatureFlag } from '@/modules/UseMyMoney/hooks';
import { useAuthStore } from '@/modules/user/stores';
import theme from '@/styles/theme';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

const useMenuItemList = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { isMobile } = useResponsive();
  const { isUseMyMoneyReimbursementEnabled } = useMyMoneyReimbursementFeatureFlag();

  const { data: accounts, hasActiveEnrollments } = useMyEnrollments(user?.id);
  const isEmployeeLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const { organizationProperties } = useOrganizationProperties();
  const isCardsHidden = useMemo(() => {
    const excludePartners = [THEMES.rippling];
    return excludePartners.includes(whiteLabelingConfig?.key || '');
  }, []);

  const isUseMyMoneyEnabled = useMemo(() => isUseMyMoneyReimbursementEnabled && 
    (isEmployeeLimitedAccess
      || organizationProperties?.isReimbursementAllowed
      || organizationProperties?.isBillPayAllowed),
    [
      isUseMyMoneyReimbursementEnabled,
      isEmployeeLimitedAccess,
      organizationProperties?.isReimbursementAllowed,
      organizationProperties?.isBillPayAllowed
    ]);

  const optionList = useMemo<Option[]>(() => {
    const quickLinksOptions: Option[] = [
      ...((organizationProperties?.isReimbursementAllowed || isEmployeeLimitedAccess) && !isUseMyMoneyReimbursementEnabled
        ? [
            {
              key: 'reimburseMe',
              title: t('Reimburse Me'),
              icon: <ReimburseMeIcon />,
              url: ROUTES.REIMBURSE_ME,
              disabled: isEmployeeLimitedAccess,
              ...(isEmployeeLimitedAccess ? { hintComponent: <LimitedAccessLabel /> } : {}),
            },
          ]
        : []),
      ...((organizationProperties?.isBillPayAllowed || isEmployeeLimitedAccess) && !isUseMyMoneyReimbursementEnabled
        ? [
            {
              key: 'requestBillPay',
              title: t('Pay a Bill'),
              icon: <PayMyBillIcon />,
              url: ROUTES.REQUEST_BILL_PAY,
              disabled: isEmployeeLimitedAccess,
              ...(isEmployeeLimitedAccess ? { hintComponent: <LimitedAccessLabel /> } : {}),
            },
          ]
        : []),
       ...(isUseMyMoneyEnabled
        ? [
            {
              key: 'useMyMoney',
              title: t('Use My Money'),
              icon: <ReimburseMeIcon {...hasActiveEnrollments ? {} : { color: "iconDisabled" }} />,
              url: ROUTES.USE_MY_MONEY,
              disabled: isEmployeeLimitedAccess || !hasActiveEnrollments,
              ...(hasActiveEnrollments ? {} : { hintComponent: <NoAccountsAvailableLabel /> }),
              ...(isEmployeeLimitedAccess ? { hintComponent: <LimitedAccessLabel /> } : {}),
              wrapperStyle: { background: theme.colors.buttonPrimary },
            },
          ]
        : []),
    ];

    const useMyMoneyOptions: Option[] = isMobile
      ? [
          {
            key: 'useMyMoney',
            title: t('Use My Money'),
            icon: <ReimburseMeIcon />,
            dropdownOptions: quickLinksOptions,
          },
        ]
      : quickLinksOptions;

    const myDocumentsOption: Option = {
      key: 'myDocuments',
      title: t('My Documents'),
      icon: <MyDocumentsIcon />,
      url: ROUTES.MY_DOCUMENTS,
      disabled: isEmployeeLimitedAccess,
      ...(isEmployeeLimitedAccess ? { hintComponent: <LimitedAccessLabel /> } : {}),
      options: [],
    };

    const useMyMoneyWithLabelOptions: Option[] = [
      ...(quickLinksOptions.length > 0
        ? [
            {
              key: 'quickLinks',
              title: t('Quick Links'),
              delimiter: true,
            },
            ...useMyMoneyOptions,
          ]
        : []),
    ];

    const allOptions: Option[] = [
      {
        key: 'home',
        title: t('Home'),
        icon: <HomeOutlineIcon />,
        url: '/',
      },
      {
        key: 'expenseDetails',
        title: t('Expense Details'),
        icon: <ExpensesDetailsIcon />,
        url: ROUTES.EXPENSE_DETAILS,
      },
      {
        key: 'myAccounts',
        title: t('My Accounts'),
        icon: <MyAccountsIcon />,
        url: ROUTES.MY_ACCOUNTS,
        options: accounts?.map((item) => ({
          key: `${item.id}`,
          title: item.name,
          url: ROUTES.MY_ACCOUNTS_DETAILS(item.id),
        })),
      },
      // {
      //   key: 'investments',
      //   title: t('Investments'),
      //   icon: <FontIcon type="investments-outline" />,
      //   url: `${ROUTES.INVESTMENTS}?id=${accounts?.[0]?.id}`,
      // },
      ...(isCardsHidden
        ? []
        : [
            {
              key: 'cards',
              title: t('Cards'),
              icon: <CardsItemIcon />,
              url: ROUTES.CARDS,
              options: [],
            },
          ]),
      ...(isMobile
        ? [...useMyMoneyWithLabelOptions, myDocumentsOption]
        : [myDocumentsOption, ...useMyMoneyWithLabelOptions]),
      // {
      //   key: 'payMyBills',
      //   title: t('Pay My Bills'),
      //   icon: <FontIcon type="pay-my-bill" />,
      //   url: '/transaction/pay-my-bills',
      // },
      // {
      //   key: 'settings',
      //   title: t('Settings'),
      //   icon: <FontIcon type="settings-outline" />,
      //   url: '/settings',
      // },
      // {
      //   key: 'statements',
      //   title: t('Statements'),
      //   icon: <FontIcon type="statements-outline" />,
      //   url: '/statements',
      // },
      // {
      //   key: 'dev-playground',
      //   title: t('Dev playground'),
      //   icon: <FontIcon type="scan" />,
      //   url: '/dev-playground',
      // },
    ];

    return allOptions;
  }, [
    organizationProperties.isReimbursementAllowed,
    organizationProperties.isBillPayAllowed,
    isEmployeeLimitedAccess,
    t,
    accounts,
    isCardsHidden,
    isMobile,
    isUseMyMoneyEnabled,
    isUseMyMoneyReimbursementEnabled,
    hasActiveEnrollments,
  ]);

  return {
    formattedList: optionList || [],
  };
};

export default useMenuItemList;
